import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api from "./api";
import ProductHistory from "./ProductHistory";
import Lightbox from "react-image-lightbox";
import "react-image-lightbox/style.css";

const ProductDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation(); // mamy location.search (query)
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  useEffect(() => {
    api
      .get(`${process.env.REACT_APP_API_URL}/products/${id}`, { withCredentials: true })
      .then((res) => setProduct(res.data))
      .catch(() => setError("❌ Nie udało się załadować danych produktu."));
  }, [id]);

  const handleDelete = () => {
    if (window.confirm("Czy na pewno chcesz usunąć ten produkt?")) {
      api
        .delete(`${process.env.REACT_APP_API_URL}/products/${product.id}`, { withCredentials: true })
        .then(() => navigate(`/products${location.search || ""}`))
        .catch(() => alert("Nie udało się usunąć produktu."));
    }
  };

  const toggleFlag = async () => {
    try {
      const res = await api.patch(
        `${process.env.REACT_APP_API_URL}/products/${id}/flag`,
        {},
        { withCredentials: true }
      );
      const is_flagged = res.data?.is_flagged ?? !product.is_flagged;
      setProduct((prev) => ({ ...prev, is_flagged }));
    } catch (e) {
      alert("❌ Nie udało się zmienić flagi.");
    }
  };

  if (error) return <div className="alert alert-danger mt-4">{error}</div>;
  if (!product) return <div className="p-4">Wczytywanie...</div>;

  const imageUrls = product.images?.map((img) => img.full_url) || [];
  const auctionPlatforms = Array.isArray(product.auction_platform)
    ? product.auction_platform
    : (product.auction_platform || "").split(",");

  return (
    <div className="container-xl py-4">
      <div className="row g-4">
        <div className="col-12 col-lg-6">
          {/* przycisk powrotu z zachowaniem filtrów */}
          <div className="mb-3">
            <Link
              to={`/products${location.search || ""}`}
              className="btn btn-outline-secondary"
              title="Powrót do listy z zachowaniem filtrów"
            >
              ⬅️ Powrót do listy
            </Link>
          </div>

          <h2 className="mb-3 d-flex align-items-center gap-2">
            {product.name}
            {product.is_flagged ? (
              <span className="badge bg-warning text-dark" title="Produkt oflagowany">⭐ Oflagowany</span>
            ) : (
              <span className="badge bg-secondary" title="Brak flagi">Brak flagi</span>
            )}
          </h2>

          <ul className="list-group mb-3">
            <li className="list-group-item">
              <i className="fas fa-flag me-2 text-danger" /> <strong>Status flagi:</strong>{" "}
              {product.is_flagged ? "Oflagowany" : "Brak flagi"}
            </li>
            <li className="list-group-item">
              <i className="fas fa-hashtag me-2 text-secondary" /> <strong>Nr katalogowy:</strong> {product.catalog_number}
            </li>
            <li className="list-group-item">
              <i className="fas fa-cogs me-2 text-secondary" /> <strong>Model:</strong> {product.model}
            </li>
            <li className="list-group-item">
              <i className="fas fa-warehouse me-2 text-secondary" /> <strong>St.M:</strong> {product.location}
            </li>
            <li className="list-group-item">
              <i className="fas fa-sort-numeric-up me-2 text-secondary" /> <strong>Ilość:</strong> {product.quantity}
            </li>
            <li className="list-group-item">
              <i className="fas fa-ruler me-2 text-secondary" /> <strong>Jednostka:</strong> {product.unit}
            </li>
            <li className="list-group-item">
              <i className="fas fa-money-bill-wave me-2 text-secondary" /> <strong>Cena:</strong>{" "}
              {Number(product.price || 0).toFixed(2)} zł{" "}
              {product.price_type === "up" && <span className="text-success">⬆️</span>}
              {product.price_type === "down" && <span className="text-danger">⬇️</span>}
              {product.price_type === "constant" && <span className="text-muted">➖</span>}
            </li>
            <li className="list-group-item">
              <i className="fas fa-align-left me-2 text-secondary" /> <strong>Opis:</strong> {product.description}
            </li>
            <li className="list-group-item">
              <i className="fas fa-calendar-plus me-2 text-secondary" /> <strong>Data dodania:</strong>{" "}
              {new Date(product.created_at).toLocaleString("pl-PL")}
            </li>

            {auctionPlatforms.includes("allegro") && product.auction_allegro_url && (
              <li className="list-group-item">
                <i className="fab fa-algolia me-2 text-warning" /> <strong>Allegro:</strong>{" "}
                <a href={product.auction_allegro_url} target="_blank" rel="noreferrer">
                  {product.auction_allegro_url}
                </a>
              </li>
            )}
            {auctionPlatforms.includes("olx") && product.auction_olx_url && (
              <li className="list-group-item">
                <i className="fas fa-store me-2 text-primary" /> <strong>OLX:</strong>{" "}
                <a href={product.auction_olx_url} target="_blank" rel="noreferrer">
                  {product.auction_olx_url}
                </a>
              </li>
            )}
          </ul>

          <div className="d-flex gap-2 flex-wrap">
            <button
              className={`btn ${product.is_flagged ? "btn-warning" : "btn-outline-warning"}`}
              onClick={toggleFlag}
              title={product.is_flagged ? "Odflaguj produkt" : "Oflaguj produkt"}
            >
              {product.is_flagged ? "⭐ Odflaguj" : "☆ Oflaguj"}
            </button>

            <button
              className="btn btn-warning"
              onClick={() => navigate(`/products/${product.id}/edit${location.search || ""}`)}
            >
              ✏️ Edytuj
            </button>
            <button className="btn btn-danger" onClick={handleDelete}>
              🗑️ Usuń
            </button>
          </div>
        </div>

        {/* Zdjęcia */}
        <div className="col-12 col-lg-6">
          {Array.isArray(product.images) && product.images.length > 0 && (
            <>
              <h5 className="mb-3">📷 Zdjęcia:</h5>
              <div className="d-flex flex-wrap gap-3">
                {product.images.map((img, i) => (
                  <img
                    key={i}
                    src={img.full_url}
                    alt={`Zdjęcie ${i + 1}`}
                    className="img-thumbnail"
                    style={{
                      width: "150px",
                      height: "150px",
                      objectFit: "cover",
                      cursor: "pointer"
                    }}
                    onClick={() => {
                      setLightboxIndex(i);
                      setLightboxOpen(true);
                    }}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        <ProductHistory productId={product.id} />

        {lightboxOpen && (
          <Lightbox
            mainSrc={imageUrls[lightboxIndex]}
            nextSrc={imageUrls[(lightboxIndex + 1) % imageUrls.length]}
            prevSrc={imageUrls[(lightboxIndex + imageUrls.length - 1) % imageUrls.length]}
            onCloseRequest={() => setLightboxOpen(false)}
            onMovePrevRequest={() =>
              setLightboxIndex((lightboxIndex + imageUrls.length - 1) % imageUrls.length)
            }
            onMoveNextRequest={() =>
              setLightboxIndex((lightboxIndex + 1) % imageUrls.length)
            }
            imageTitle={`Zdjęcie ${lightboxIndex + 1} z ${imageUrls.length}`}
          />
        )}
      </div>
    </div>
  );
};

export default ProductDetails;
